<script lang="ts">
  /**
   * SetsTab — the second of four tabs (Scott, 2026-09-10: "The second tab
   * should be the Set tab (not Scores)"). Was ScoresTab. One scroll:
   *   1. Header (title + subtitle)
   *   2. "Your sets · getting faster?" — first / fastest / last per named set
   *      and one stroke-colored bar per swim (YourSetsSection; spec item 3
   *      "the speed thread", 2026-09-01). Its tap row opens the SetsDiscovered
   *      sheet. Then My sets.
   *
   * Nothing above Your sets: the week ring, weeks in a row, the 8-week chart
   * and the Your scores tiles are one tap away in the scores drawer (R6). The
   * stroke cards and Glide trend are on Strokes; the per-swim charts ("This
   * swim") are at the bottom of Swim (Q1).
   *
   * Coverage honesty: the rows come from the swimFacts cache (built by the
   * background scan; nothing here triggers a re-derivation). Partial coverage
   * is labeled "Counted from N of M swims so far.", never an all-time claim.
   */
  import { onMount } from "svelte";
  import { workouts, loadWorkouts } from "../lib/workoutsStore";
  import YourSetsSection from "./YourSetsSection.svelte";
  import OpenSetNotice from "../lib/deepLink/OpenSetNotice.svelte";
  import MySetsSection from "../lib/mySets/MySetsSection.svelte";
  import { setHistoryRows } from "../lib/setHistory";
  import { medianDurationByStroke } from "../lib/recordEligibility";
  import { getAllSwimFacts, setsCoverage } from "../lib/allTimeAggregates";

  onMount(() => { void loadWorkouts(); });

  // The caches are localStorage reads, not stores. Re-read when the workout
  // list lands so the cards refresh once after boot; a scan finishing while
  // this tab is open shows up on the next visit.
  let facts = $derived.by(() => {
    void $workouts;
    return getAllSwimFacts();
  });

  // One all-history median per stroke feeds the set records' floor.
  let medians = $derived(medianDurationByStroke(facts));
  let setRows = $derived(setHistoryRows(facts, medians));
  let setsCov = $derived(setsCoverage(facts, $workouts.length));
</script>

<section class="sets-tab" aria-label="Sets">
  <div class="tab-head">
    <h1 class="tab-title">Sets</h1>
  </div>

  <OpenSetNotice />

  <YourSetsSection rows={setRows} coverage={setsCov} />
  <MySetsSection />
</section>

<style>
  .sets-tab {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: transparent;
    border: 0;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  /* Header: the title alone, like Strokes (VP round 3 cut the subtitle). */
  .tab-head {
    padding: var(--space-2) 0 0;
  }
  .tab-title {
    font: var(--type-lede);
    letter-spacing: -0.01em;
    color: var(--text-primary);
    margin: 0;
  }
</style>
